// native
import { Injectable } from '@angular/core';
import { map, catchError, retry, timeout } from 'rxjs/operators';
import { empty } from 'rxjs';

// service
import { ApiService } from './api.service';
import { GlobalsService } from './globals.service';
import { ToastService } from './toast.service';

// models
import { VersionResponse } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class VersionService {

  browserPrefixes: string[] = ['moz', 'ms', 'o', 'webkit'];
  isVisible = true;
  browserPrefix: string;
  hiddenPropertyName: string;
  visibilityEventName: string;
  isVersionCheckPending: boolean = false;

  constructor(
    private apiService: ApiService,
    private globalsService: GlobalsService,
    private toastService: ToastService
  ) {
    this.browserPrefix = this.getBrowserPrefix();
    this.hiddenPropertyName = this.getHiddenPropertyName(this.browserPrefix);
    this.visibilityEventName = this.getVisibilityEvent(this.browserPrefix);

    // set global variable that consumer's JSONP will call if consumer provides version (GDocs)
    window['getVersionFromConsumer'] = (params) => this.getVersionFromConsumer(params);
  }

  private getHiddenPropertyName(prefix: string) {
    return (prefix ? prefix + 'Hidden' : 'hidden');
  }

  private getVisibilityEvent(prefix) {
    return (prefix ? prefix : '') + 'visibilitychange';
  }

  private getBrowserPrefix() {
    for (let i = 0; i < this.browserPrefixes.length; i++) {
      if (this.getHiddenPropertyName(this.browserPrefixes[i]) in document) {
        return this.browserPrefixes[i];
      }
    }

    return null;
  }

  private onVisible() {
    if (this.isVisible) {
      return;
    }
    this.isVisible = true;
    console.log('visible');
    this.getVersionFile();
  }

  private onHidden() {
    if (!this.isVisible) {
      return;
    }
    this.isVisible = false;
  }

  private handleVisibilityChange(isFocus: boolean = false, isBlur: boolean = false) {
    if (isFocus) {
      return this.onVisible();
    }

    if (isBlur) {
      return this.onHidden();
    }

    if (document[this.hiddenPropertyName]) {
      return this.onHidden();
    }

    return this.onVisible();
  }

  public getVersionFile() {
    if (this.globalsService.appConfig.getVersion) {
      // delayed because of delay in version file deploy and app deploy on Google
      if (this.isVersionCheckPending)
        return;

      this.isVersionCheckPending = true;
      setTimeout(() => {
        this.isVersionCheckPending = false;
        this.globalsService.appConfig.getVersion();
      }, this.globalsService.appConfig.environment.production ? 600000 : 120000);
      return;
    }

    this.getVersionFromApi();
  }

  getVersionFromConsumer(versionPayload): void {
    this.isNewVersion(versionPayload) && this.showNewVersionInfoToast();
  }

  getVersionFromApi(): void {
    this.apiService.get(`${this.globalsService.appConfig.environment.baseUrls.smartciteApp}/version.json`)
      .pipe(
        map((versionPayload: VersionResponse) => {
          return this.isNewVersion(versionPayload);
        }),
        catchError(() => empty()),
        timeout(5000),
        retry(3)
      )
      .subscribe((isNewVersion: boolean) => {
        isNewVersion && this.showNewVersionRefreshToast();
      });
  }

  isNewVersion(versionPayload: VersionResponse): boolean {
    // SC Word will check build time and SC Docs will check build version
    if (this.globalsService.appConfig.buildTime) {
      const latestBuildTime = new Date(versionPayload.time * 1000);
      const clientBuildTime = new Date(this.globalsService.appConfig.buildTime * 1000);

      const isLaterBuildTime = latestBuildTime.getTime() > clientBuildTime.getTime();

      return isLaterBuildTime;
    }

    const isDifferentVersion = versionPayload.version !== this.globalsService.appConfig.buildVersion;
    return isDifferentVersion;
  }

  showNewVersionRefreshToast(): void {
    const confirmCallback = () => {
      window.location.reload();
    };

    this.toastService.openToast({
      text: `There is a new version of SmartCite available. Please click OK to update. 
        If you have any pending citations to add or update in the document, you can do that first.`,
      type: 'error',
      confirmButton: {
        text: 'OK',
        callback: confirmCallback
      },
      autoClose: false,
      closeDisabled: true,
      isCentered: true
    });
  }

  showNewVersionInfoToast(): void {
    this.toastService.openToast({
      text: `There is a new version of SmartCite available. You can load the new version by refreshing your Google Docs tab and then reloading the Smartcite Add-on. 
        If you have any pending citations to add or update in the document, you can do that first.`,
      type: 'error',
      autoClose: false,
      isCentered: true
    });
  }

  setCheckNewVersionListener() {
    document.addEventListener(this.visibilityEventName, () => this.handleVisibilityChange(), false);

    document.addEventListener('focus', () => this.handleVisibilityChange(true, false), false);
    document.addEventListener('blur', () => this.handleVisibilityChange(false, true), false);

    window.addEventListener('focus', () => this.handleVisibilityChange(true, false), false);
    window.addEventListener('blur', () => this.handleVisibilityChange(false, true), false);
  }
}